import React from 'react';
import { styled, useStyletron } from '../Themes';

const SvgIconRoot = ({ size = "small", color, className, ...props }: any) => {
    const [css]: any = useStyletron();

    const SizesMap: { [key: string]: string } = {
        'x-small': 'var(--epods--icon-x-small)',
        small: '19px',
        medium: '27px',
        large: '39px',
        'x-large': 'var(--epods--icon-x-large)',
    };
    let sizeDefinition: string = size;
    if (['x-small', 'small', 'medium', 'large', 'x-large'].includes(size)) {
        sizeDefinition = SizesMap[size];
    }
    return (<svg {...props} className={`${className} ${css({
        userSelect: 'none',
        width: 'calc(1px + ' + sizeDefinition + ')',
        height: sizeDefinition,
        display: 'inline-block',
        fill: 'currentColor',
        color: color,
        padding: '0px 0 0 0px',
        flexShrink: 0,
        fontSize: sizeDefinition,
        transform: 'rotateX(180deg)',
        margin: '-1px -2px 0px 0px'
    })}`} />)
}

export const SvgIcon = React.forwardRef((props: Omit<SvgIconProps, 'css'>, ref) => {
    const {
        children,
        className,
        color = 'currentColor',
        component = 'svg',
        fontSize = 'medium',
        htmlColor,
        titleAccess,
        viewBox = "0 0 1100 1100",
        ...other
    } = props;

    return (
        <SvgIconRoot
            as={component}
            className={className}
            size={fontSize}
            focusable="false"
            viewBox={viewBox}
            color={htmlColor || color}
            aria-hidden={titleAccess ? undefined : true}
            role={titleAccess ? 'img' : undefined}
            ref={ref}
            {...other}
        >
            {children}
            {titleAccess ? <title>{titleAccess}</title> : null}
        </SvgIconRoot>
    );
});

export type SvgIconProps = {
    /**
     * Node passed into the SVG element.
     */
    children: React.ReactNode,
    /**
     * Override or extend the styles applied to the component.
     */
    classes: object,
    /**
     * @ignore
     */
    className: string,
    /**
     * The color of the component. It supports those theme colors that make sense for this component.
     * You can use the `htmlColor` prop to apply a color attribute to the SVG element.
     * @default 'inherit'
     */
    color: 'inherit' | 'action' | 'disabled' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning',
    /**
     * The component used for the root node.
     * Either a string to use a HTML element or a component.
     */
    component: React.ReactNode,
    /**
     * The fontSize applied to the icon. Defaults to 24px, but can be configure to inherit font size.
     * @default 'medium'
     */
    fontSize: 'x-small' | 'small' | 'medium' | 'large' | 'x-large',
    /**
     * Applies a color attribute to the SVG element.
     */
    htmlColor: string,
    /**
     * The shape-rendering attribute. The behavior of the different options is described on the
     * [MDN Web Docs](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/shape-rendering).
     * If you are having issues with blurry icons you should investigate this prop.
     */
    //shapeRendering: string,
    /**
     * Provides a human-readable title for the element that contains it.
     * https://www.w3.org/TR/SVG-access/#Equivalent
     */
    titleAccess: string,
    /**
     * Allows you to redefine what the coordinates without units mean inside an SVG element.
     * For example, if the SVG element is 500 (width) by 200 (height),
     * and you pass viewBox="0 0 50 20",
     * this means that the coordinates inside the SVG will go from the top left corner (0,0)
     * to bottom right (50,20) and each unit will be worth 10px.
     * @default '0 0 24 24'
     */
    viewBox: string,
};
